import React, { memo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, CircularProgress } from '@mui/material';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import Button from 'modules/views/shared/Button';
import PasswordField from 'modules/views/shared/PasswordField';
import TextField from 'modules/views/shared/TextField';
import WithBranding from 'modules/views/shared/WithBranding';
import { ReactComponent as LinkExpired } from 'assets/icons/LinkExpired.svg';

import confirmResetPasswordSchema from './schema';
import useForm from 'hooks/useForm';
import useSubmitOnEnter from 'hooks/useSubmitOnEnter';
import { useConfirmResetPassword, useValidateResetPasswordToken } from './hooks';
import { useStyles } from './styles';
import { ROUTES } from 'constants/routes';
import { parseQueryParams } from 'utils';
import { useAppSelector } from 'modules/state-mgmt/store';

const ConfirmResetPassword = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const hasValidated = useRef(false);
  const { isWorker, isAdmin } = useAppSelector(state => ({
    isWorker: state.user?.userId && state.user?.isWorker,
    isAdmin: state.user?.isAdmin,
  }));

  const queryParams = parseQueryParams(location.search);
  const token = queryParams.get('token') || '';
  const email = queryParams.get('email') || '';
  const mobilePhoneNumber = queryParams.get('mobilePhoneNumber') || '';

  const { isLoading: isValidating, validateToken } = useValidateResetPasswordToken({
    onSuccess: () => {
      setIsTokenValid(true);
    },
    onFailure: () => {
      setIsTokenValid(false);
    },
  });

  const { isLoading, confirmResetPassword } = useConfirmResetPassword({
    onFailure: backendErrors => {
      setErrors(backendErrors);
    },
  });

  useEffect(() => {
    if (!hasValidated.current) {
      hasValidated.current = true;
      if (token) {
        validateToken(email, mobilePhoneNumber, token);
      } else {
        setIsTokenValid(false);
      }
    }
  }, [token, email, mobilePhoneNumber, validateToken]);

  const onSubmit = (values: any) => {
    if (!token) {
      return;
    }
    confirmResetPassword(email, mobilePhoneNumber, token, values.password);
  };

  const { values, errors, handleSubmit, setValue, setErrors } = useForm({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: confirmResetPasswordSchema(t, {
      email,
      mobilePhoneNumber,
    }),
    onSubmit: onSubmit,
  });

  const submitOnEnter = useSubmitOnEnter(handleSubmit, isLoading);

  const LoadingContent = (
    <div className={classes.loadingContainer}>
      <CircularProgress size={48} />
    </div>
  );

  const ExpiredContent = (
    <div className={classes.expiredContainer}>
      <LinkExpired className={classes.expiredIcon} />
      <div className={classes.expiredTitle}>{t('confirmResetPassword.expiredLinkMessage')}</div>
      <Button
        label={t('confirmResetPassword.buttons.backToSignIn')}
        hasEndArrow={true}
        styleClass={classes.expiredButton}
        testId="expired-link-back-to-signin-button"
        onClick={() => navigate(ROUTES.LOGIN.path)}
      />
    </div>
  );

  const FormContent = (
    <>
      <ContentHeader title={t('confirmResetPassword.title')} subtitle={t('confirmResetPassword.subtitle')} />

      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid item xs={12}>
          <PasswordField
            label={t('confirmResetPassword.password')}
            name="password"
            required={true}
            value={values.password}
            email={email}
            mobilePhoneNumber={mobilePhoneNumber}
            error={errors.password?.message}
            testId="password-input"
            onChange={e => setValue('password', e.target.value)}
            passwordMatchContainerClass={classes.passwordMatchContainer}
            maskText={isAdmin}
            autoComplete={isAdmin ? 'off' : 'new-password'}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label={t('confirmResetPassword.confirmPassword')}
            name="confirmPassword"
            required={true}
            value={values.confirmPassword}
            error={errors.confirmPassword?.message}
            type={isAdmin ? 'text' : 'password'}
            maskText={isAdmin}
            testId="confirmPassword-input"
            onChange={e => setValue('confirmPassword', e.target.value)}
            onKeyDown={submitOnEnter}
            autoComplete={isAdmin ? 'off' : 'new-password'}
          />
        </Grid>
      </Grid>

      <div className={classes.buttonParent}>
        <Button
          label={t('confirmResetPassword.buttons.continue')}
          hasEndArrow={true}
          isLoading={isLoading}
          styleClass={classes.button}
          testId="confirm-reset-password-button"
          onClick={handleSubmit}
        />
      </div>
    </>
  );

  const getContent = () => {
    if (isTokenValid === null || isValidating) {
      return LoadingContent;
    }
    if (!isTokenValid) {
      return ExpiredContent;
    }
    return FormContent;
  };

  return <WithBranding Content={getContent()} />;
};

export default memo(ConfirmResetPassword);
